// One numbered, collapsible step of the campaign builder.
//
import type { ReactNode } from 'react';
import { Icons } from '@/components/primitives';

export interface SectionProps {
  /** The step number shown in the badge until the step is done. */
  n: string | number;
  /** Also the header's accessible name, so it is text, not markup. */
  title: string;
  /** Shown under the title while the step is collapsed. */
  summary?: ReactNode;
  open: boolean;
  done: boolean;
  onToggle: () => void;
  children?: ReactNode;
  locked?: boolean;
  lockNotice?: ReactNode;
}

// `locked` makes the body inert (pointer-events: none), which is why the
// explanation goes in `lockNotice`, rendered *above* the body: a banner inside
// it cannot be clicked, so its Duplicate button was dead. Sections 4 and 5
// rendered no notice at all, so "Edit creative" looked live and swallowed the
// click (client report, 2026-09).
export function Section({
  n,
  title,
  summary,
  open,
  done,
  onToggle,
  children,
  locked = false,
  lockNotice = null,
}: SectionProps) {
  return (
    <div className={`sec ${open ? 'open' : ''} ${done && !open ? 'done' : ''}`}>
      <div
        className="sec-hd"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-label={`${title}${done ? ', complete' : ''}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        onClick={onToggle}
      >
        <div className="sec-num">
          {done && !open ? <span style={{ fontSize: 11 }}>{Icons.check}</span> : n}
        </div>
        <div style={{ flex: 1 }}>
          <div className="sec-title">{title}</div>
          {summary && !open && <div className="sec-summary">{summary}</div>}
        </div>
        <span className="sec-chev">{open ? Icons.chev_d : Icons.chev_r}</span>
      </div>
      {open && locked && lockNotice}
      {open && (
        // `data-locked`, not `aria-disabled`: a locked body still holds one
        // live control — the creative slot's "Roll out version N" (0024),
        // marked `data-lock-exempt` — and an aria-disabled ancestor would
        // announce it as unavailable and make Playwright refuse to click it.
        // Each dead control carries its own `disabled`.
        <div
          className="sec-body"
          data-locked={locked ? 'true' : undefined}
          // No dimming: an opacity on the body took its small text below AA contrast
          // (the Meta delivery policy's 9px labels, 2026-10-21), and an exempt control
          // inside could not undo it. The notice above and each control's `disabled` say
          // the section is locked.
          style={locked ? { pointerEvents: 'none' } : undefined}
        >
          {children}
        </div>
      )}
    </div>
  );
}
